import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import ScoreRing from "../components/ScoreRing";
import SendSmsDialog from "../components/SendSmsDialog";
import { toast } from "sonner";
import { ArrowLeft, Send, Pencil, X, Copy, AlertTriangle, Trash2, Loader2, CheckCircle2, CircleDashed, XCircle, RefreshCw } from "lucide-react";

const severityStyles = {
  low: "border-l-[hsl(var(--muted-foreground))]",
  medium: "border-l-[hsl(var(--warning))]",
  high: "border-l-[hsl(var(--destructive))]",
};

const DELIVERY_LABEL = {
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
};

const DELIVERY_ICON = {
  sending: Loader2,
  sent: CircleDashed,
  delivered: CheckCircle2,
  failed: XCircle,
};

const DELIVERY_COLOR = {
  sending: "text-muted-foreground",
  sent: "text-primary",
  delivered: "text-[hsl(var(--success))]",
  failed: "text-destructive",
};

const TERMINAL_DELIVERY = new Set(["delivered", "failed"]);

const subScoreItems = [
  { key: "tone", label: "Tone" },
  { key: "clarity", label: "Clarity" },
  { key: "emotional_intensity", label: "Emotional intensity" },
  { key: "misunderstanding_risk", label: "Misunderstanding risk" },
  { key: "intent_alignment", label: "Intent alignment" },
];

function ScoreBar({ value }) {
  const clamped = Math.max(0, Math.min(100, value ?? 0));
  const color =
    clamped >= 75 ? "bg-[hsl(var(--success))]"
    : clamped >= 50 ? "bg-[hsl(var(--warning))]"
    : "bg-[hsl(var(--destructive))]";
  return (
    <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
      <div className={`h-full ${color} rounded-full`} style={{ width: `${clamped}%`, transition: "width 600ms ease-out" }} />
    </div>
  );
}

export default function Review() {
  const { id } = useParams();
  const nav = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [smsConfigured, setSmsConfigured] = useState(false);
  const [chosenText, setChosenText] = useState("");
  const [chosenTo, setChosenTo] = useState("");
  const [isRetry, setIsRetry] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, cfg] = await Promise.all([
          api.get(`/reviews/${id}`),
          api.get(`/config/sms`).catch(() => ({ data: { configured: false } })),
        ]);
        setReview(data);
        setChosenText(data.message);
        setSmsConfigured(!!cfg.data?.configured);
      } catch (err) {
        toast.error("Review not found");
        nav("/app/reviews", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  // Poll for delivery status updates while non-terminal
  useEffect(() => {
    const clear = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    if (!review?.delivery_status || TERMINAL_DELIVERY.has(review.delivery_status)) {
      clear();
      return clear;
    }
    if (pollRef.current) return clear;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/reviews/${id}`);
        setReview(data);
      } catch { /* ignore */ }
    }, 3500);
    return clear;
  }, [id, review?.delivery_status]);

  const decide = async (status, final_message = null) => {
    setActing(true);
    try {
      const { data } = await api.patch(`/reviews/${id}/decision`, { status, final_message });
      setReview(data);
      if (status === "sent") toast.success("Marked as sent — remember, FINALLOOK didn't send anything for you.");
      if (status === "edited") toast.success("Marked as edited");
      if (status === "cancelled") toast.success("Marked as cancelled");
    } catch {
      toast.error("Could not update review");
    } finally {
      setActing(false);
    }
  };

  const remove = async () => {
    if (!confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/reviews/${id}`);
      toast.success("Review deleted");
      nav("/app/reviews", { replace: true });
    } catch {
      toast.error("Could not delete review");
    }
  };

  const openSmsDialog = (text) => {
    setChosenText(text);
    setChosenTo("");
    setIsRetry(false);
    setSmsOpen(true);
  };

  // "Use this → Edit message" for a suggested rewrite.
  // Places the suggestion into the Composer's Message field, preserves the
  // original review's recipient and goal, and lets the user edit before
  // running a completely fresh FINALLOOK. Never sends.
  const handleUseAndEdit = (text) => {
    decide("edited");
    nav("/app/compose", {
      state: {
        prefill: text,
        recipient: review?.recipient || "",
        goal: review?.goal || "",
      },
    });
  };

  const openRetryDialog = () => {
    // Pre-fill with the exact previously approved recipient + final message.
    // User must still confirm both before FINALLOOK retries the send.
    setChosenText(review.final_message || review.message);
    setChosenTo(review.sent_to || "");
    setIsRetry(true);
    setSmsOpen(true);
  };

  const sendSms = async ({ to_number, message }) => {
    setSending(true);
    try {
      const { data } = await api.post(`/reviews/${id}/send`, { to_number, message });
      toast.success(`Sent to ${data.to}`);
      setSmsOpen(false);
      // Refresh review to show updated status
      const { data: updated } = await api.get(`/reviews/${id}`);
      setReview(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SMS send failed");
    } finally {
      setSending(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="review-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading review…
      </div>
    );
  }
  if (!review) return null;

  const { result } = review;

  return (
    <div data-testid="review-screen">
      <button
        onClick={() => nav("/app")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        data-testid="review-back-btn"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Score */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 mb-6" data-testid="review-score-card">
        <div className="flex flex-col items-center text-center">
          <ScoreRing score={result.scores.overall} label="Overall" />
          <p className="mt-6 text-sm text-muted-foreground uppercase tracking-[0.15em]">Tone</p>
          <p className="font-heading text-lg font-semibold mt-1" data-testid="review-tone">{result.tone_description}</p>
          <p className="text-sm text-foreground/80 leading-relaxed mt-4 max-w-md" data-testid="review-summary">
            {result.summary}
          </p>
        </div>
      </div>

      {/* Sub-scores */}
      <div className="rounded-2xl border border-border bg-card p-6 mb-6" data-testid="review-subscores">
        <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">Breakdown</h3>
        <ul className="space-y-4">
          {subScoreItems.map((s) => (
            <li key={s.key} data-testid={`review-subscore-${s.key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-foreground/90">{s.label}</span>
                <span className="text-sm font-medium text-muted-foreground font-mono">
                  {result.scores[s.key]}
                </span>
              </div>
              <ScoreBar value={result.scores[s.key]} />
            </li>
          ))}
        </ul>
      </div>

      {/* Issues */}
      {result.issues && result.issues.length > 0 && (
        <div className="mb-6" data-testid="review-issues">
          <h3 className="font-heading text-lg font-semibold tracking-tight mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
            Potential issues
          </h3>
          <ul className="space-y-3">
            {result.issues.map((issue, i) => (
              <li
                key={i}
                className={`rounded-xl bg-card border border-border border-l-4 ${severityStyles[issue.severity] || severityStyles.medium} px-4 py-3`}
                data-testid={`review-issue-${i}`}
              >
                <p className="font-medium text-sm mb-1">{issue.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{issue.description}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Original */}
      <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="review-original">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Original</span>
          <button
            onClick={() => copy(review.message)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="review-copy-original"
          >
            <Copy className="h-3 w-3" /> Copy
          </button>
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{review.message}</p>
        {(review.recipient || review.goal) && (
          <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2 text-xs text-muted-foreground">
            {review.recipient && <span className="pill-tag">To: {review.recipient}</span>}
            {review.goal && <span className="pill-tag">Goal: {review.goal}</span>}
          </div>
        )}
      </div>

      {/* Suggestions */}
      {result.suggestions && result.suggestions.length > 0 && (
        <div className="mb-6" data-testid="review-suggestions">
          <h3 className="font-heading text-lg font-semibold tracking-tight mb-3">FINALLOOK suggestions</h3>
          <ul className="space-y-3">
            {result.suggestions.map((s, i) => (
              <li key={i} className="rounded-2xl border border-border bg-card p-5" data-testid={`review-suggestion-${s.style}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="pill-tag border-primary/30 text-primary">{s.label}</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => copy(s.text)}
                      className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                      data-testid={`review-copy-suggestion-${s.style}`}
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                    <button
                      onClick={() => handleUseAndEdit(s.text)}
                      className="text-xs text-primary font-medium hover:underline inline-flex items-center gap-1"
                      data-testid={`review-use-suggestion-${s.style}`}
                    >
                      <Pencil className="h-3 w-3" /> Use &amp; edit
                    </button>
                  </div>
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{s.text}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Decision */}
      <div className="rounded-2xl border border-border bg-card p-5 mb-8" data-testid="review-decision">
        <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">Your decision</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button
            onClick={() => openSmsDialog(review.message)}
            disabled={acting}
            className="rounded-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
            data-testid="review-send-btn"
          >
            <Send className="h-4 w-4 mr-2" /> Send via SMS
          </Button>
          <Button
            variant="outline"
            disabled={acting}
            onClick={() => { decide("edited"); nav("/app/compose", { state: { prefill: review.message } }); }}
            className="rounded-full h-11 border-border font-semibold w-full"
            data-testid="review-edit-btn"
          >
            <Pencil className="h-4 w-4 mr-2" /> Edit message
          </Button>
          <Button
            variant="ghost"
            onClick={() => decide("cancelled")}
            disabled={acting}
            className="rounded-full h-11 text-muted-foreground hover:text-foreground font-semibold"
            data-testid="review-cancel-btn"
          >
            <X className="h-4 w-4 mr-2" /> Cancel
          </Button>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          {smsConfigured
            ? "You approve the exact text before it leaves. FINALLOOK will not send anything without your tap."
            : "SMS sending is turned off until Twilio credentials are added to the backend. Tap Send to see setup steps."}
        </p>
        {review.status !== "pending" && (
          <div className="mt-4 pt-4 border-t border-border" data-testid="review-current-status">
            {review.delivery_status ? (
              (() => {
                const Icon = DELIVERY_ICON[review.delivery_status] || CircleDashed;
                const color = DELIVERY_COLOR[review.delivery_status] || "text-muted-foreground";
                const label = DELIVERY_LABEL[review.delivery_status] || review.delivery_status;
                const spinning = review.delivery_status === "sending";
                return (
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className={`inline-flex items-center gap-2 text-sm font-medium ${color}`} data-testid="review-delivery-status">
                      <Icon className={`h-4 w-4 ${spinning ? "animate-spin" : ""}`} />
                      <span>{label}</span>
                      {review.sent_to && (
                        <span className="text-muted-foreground font-normal font-mono text-xs">→ {review.sent_to}</span>
                      )}
                    </div>
                    {review.delivered_at && (
                      <span className="text-xs text-muted-foreground">
                        {new Date(review.delivered_at).toLocaleString()}
                      </span>
                    )}
                    {review.delivery_status === "failed" && (
                      <Button
                        size="sm"
                        onClick={openRetryDialog}
                        disabled={sending}
                        className="rounded-full h-8 px-4 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold"
                        data-testid="review-retry-btn"
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Try again
                      </Button>
                    )}
                  </div>
                );
              })()
            ) : (
              <p className="text-xs text-primary font-medium">
                Marked as {review.status}
              </p>
            )}
            {review.delivery_error && (
              <p className="mt-2 text-xs text-destructive" data-testid="review-delivery-error">{review.delivery_error}</p>
            )}
            {Array.isArray(review.delivery_history) && review.delivery_history.length > 1 && (
              <div className="mt-4 pt-3 border-t border-border" data-testid="review-delivery-history">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
                  Delivery attempts ({review.delivery_history.length})
                </p>
                <ul className="space-y-2">
                  {[...review.delivery_history].reverse().map((h, i) => {
                    const HIcon = DELIVERY_ICON[h.delivery_status] || CircleDashed;
                    const hcolor = DELIVERY_COLOR[h.delivery_status] || "text-muted-foreground";
                    const hlabel = DELIVERY_LABEL[h.delivery_status] || h.delivery_status;
                    const attemptNum = review.delivery_history.length - i;
                    return (
                      <li key={i} className="flex items-start gap-3 text-xs" data-testid={`review-delivery-attempt-${attemptNum}`}>
                        <span className={`inline-flex items-center gap-1.5 font-medium ${hcolor} shrink-0 min-w-[6rem]`}>
                          <HIcon className="h-3.5 w-3.5" />
                          {hlabel}
                        </span>
                        <div className="flex-1 text-muted-foreground">
                          <div className="flex flex-wrap items-center gap-x-2">
                            <span>Attempt {attemptNum}</span>
                            <span>·</span>
                            <span>{new Date(h.sent_at).toLocaleString()}</span>
                            {h.delivered_at && (
                              <>
                                <span>·</span>
                                <span>delivered {new Date(h.delivered_at).toLocaleString()}</span>
                              </>
                            )}
                          </div>
                          {h.error && <div className="text-destructive mt-0.5">{h.error}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <SendSmsDialog
        open={smsOpen}
        onOpenChange={setSmsOpen}
        defaultTo={chosenTo}
        defaultMessage={chosenText}
        onSend={sendSms}
        sending={sending}
        smsConfigured={smsConfigured}
        isRetry={isRetry}
      />

      <div className="flex justify-end mb-16">
        <button
          onClick={remove}
          className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-destructive"
          data-testid="review-delete-btn"
        >
          <Trash2 className="h-3.5 w-3.5" /> Delete this review
        </button>
      </div>
    </div>
  );
}
